import { createSlice } from "@reduxjs/toolkit";
import { dispatch } from "../store";
import axios from 'axios';


// Redux

// ----------------------------------------------------------------------

let initialState = {
    tasks: [],
    assignees: [],
    isAssigneeLoading: false,
    isLoading: false,
    showSuccess: false,
    showError: false,
    errorMessage: null,
    error: null,
    profilePictureCache: {}, // Cache for profile pictures
    profilePicturePromises: {}, // Track ongoing requests to prevent duplicates
};

export const slice = createSlice({
    name: "dashboard",
    initialState,
    reducers: {
        // set filter values in redux
        settaskdata(state, action) {
            state.tasks = action.payload;
            state.isLoading = false;
        },

        getAssignees(state, action) {
            state.assignees = action.payload.assignees;
            state.isAssigneeLoading = false;
        },

        startAssigneeLoading(state) {
            state.isAssigneeLoading = true;
        },

        startLoading(state) {
            state.isLoading = true;
        },

        setShowSuccess(state, action) {
            state.showSuccess = action.payload;
        },

        setShowError(state, action) {
            state.showError = action.payload;
        },

        setErrorMessage(state, action) {
            state.errorMessage = action.payload;
        },

        setProfilePictureCache(state, action) {
            const { url, imageUrl } = action.payload;
            state.profilePictureCache[url] = imageUrl;
        },

        setProfilePicturePromise(state, action) {
            const { url, promise } = action.payload;
            state.profilePicturePromises[url] = promise;
        },

        removeProfilePicturePromise(state, action) {
            const { url } = action.payload;
            delete state.profilePicturePromises[url];
        },

        clearProfilePictureCache(state) {
            // Revoke all blob URLs to prevent memory leaks
            Object.values(state.profilePictureCache).forEach(imageUrl => {
                if (imageUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(imageUrl);
                }
            });
            state.profilePictureCache = {};
            state.profilePicturePromises = {};
        },

        // HAS ERROR
        hasError(state, action) {
            state.isLoading = false;
            state.isAssigneeLoading = false;
            state.error = action.payload;
            state.showError = true;
            state.errorMessage = action.payload?.response?.data?.message || 'An error occurred';
        },

    },

});

export const {
    startLoading,
    setShowSuccess,
    setShowError,
    setErrorMessage,
    setProfilePictureCache,
    setProfilePicturePromise,
    removeProfilePicturePromise,
    clearProfilePictureCache
} = slice.actions

export default slice.reducer;

export function getTasks() {
    const API_URL = process.env.REACT_APP_API_URL;
    return async () => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await axios.get(`${API_URL}/api/bid/opportunity/task/all`);
            dispatch(slice.actions.settaskdata(response.data.data));
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getAssignees() {
    const API_URL = process.env.REACT_APP_API_URL;
    return async () => {
        dispatch(slice.actions.startAssigneeLoading());
        try {
            const response = await axios.get(`${API_URL}/api/bid/opportunity/assignee`);
            dispatch(slice.actions.getAssignees(response.data.data));
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function handleSubmitFee(modalData) {
    const API_URL = process.env.REACT_APP_API_URL;
    console.log('Data submitted from the modal:', modalData);

    return async () => {
        try {
            // Sending API
            const response = await axios.post(`${API_URL}/api/bid/opportunity/submit/fee`,
                modalData);

            // Handle the response, if successful
            console.log(response);

            return response;
        } catch (error) {
            // Handle any errors that occur during the fetch
            console.error(error);
            dispatch(slice.actions.hasError(error));
            throw error; // Re-throw to allow component to handle it
        }
    }
};

export function uploadDoc(file, jobNumber, address, deadlineID, deadlineType, uploadID, uploadType, uploadedBy, comment) {
    const API_URL = process.env.REACT_APP_API_URL;

    console.log(file)

    const formData = {
        file,
        jobNumber: Number(jobNumber),
        address,
        deadlineID: Number(deadlineID),
        deadlineType,
        uploadID: Number(uploadID),
        uploadType,
        uploadedBy,
        comment
    }
    // const formData = new FormData();
    // formData.append("file", file);
    // formData.append("jobNumber", Number(jobNumber));
    // formData.append("address", address);
    // formData.append("deadlineID", Number(deadlineID));
    // formData.append("deadlineType", deadlineType);
    // formData.append("uploadID", Number(uploadID));
    // formData.append("uploadType", uploadType);
    // formData.append("uploadedBy", uploadedBy);
    // formData.append("comment", comment);

    return async () => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await axios.post(`${API_URL}/api/bid/opportunity/task/upload`,
                formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
            );

            dispatch(getTasks());

            return response;
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function uploadDocsBatch(files, jobNumber, address, deadlineID, deadlineType, uploadID, uploadType, uploadedBy, comment, shouldMergePDFs = true) {
    const API_URL = process.env.REACT_APP_API_URL;

    console.log('Batch upload files:', files);
    console.log('Should merge PDFs:', shouldMergePDFs);

    // Create FormData for multiple files
    const formData = new FormData();

    // Add metadata
    formData.append("jobNumber", Number(jobNumber));
    formData.append("address", address);
    formData.append("deadlineID", Number(deadlineID));
    formData.append("deadlineType", deadlineType);
    formData.append("uploadID", Number(uploadID));
    formData.append("uploadType", uploadType);
    formData.append("uploadedBy", uploadedBy);
    formData.append("comment", comment);
    formData.append("mergePDFs", shouldMergePDFs); // Add merge flag for backend

    // Add all files with unique keys
    files.forEach((file, index) => {
        formData.append(`file${index}`, file);
    });

    return async () => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await axios.post(`${API_URL}/api/bid/opportunity/task/upload/batch`,
                formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
            );

            dispatch(getTasks());

            return response;
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
            throw error; // Re-throw to allow component to handle it
        }
    };
}

export function changeDeadline(jobNumber, deadlineID, deadlineType, newDeadlineDate, updatedBy) {
    const API_URL = process.env.REACT_APP_API_URL;

    const formData = new FormData();
    formData.append("jobNumber", Number(jobNumber));
    formData.append("deadlineID", Number(deadlineID));
    formData.append("deadlineType", deadlineType);
    formData.append("newDeadlineDate", newDeadlineDate);
    formData.append("updatedBy", updatedBy);

    return async () => {
        try {
            const response = await axios.post(`${API_URL}/api/bid/opportunity/task/change/deadline`,
                formData, {
                headers: {
                    "Content-Type": "application/json",
                },
            }
            );

            dispatch(getTasks());

            return response;
        } catch (error) {
            console.log(error)
            console.error(error);
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function completeTask(jobNumber, deadlineID, deadlineType, updatedBy) {
    const API_URL = process.env.REACT_APP_API_URL;

    const formData = new FormData();
    formData.append("jobNumber", Number(jobNumber));
    formData.append("deadlineID", Number(deadlineID));
    formData.append("deadlineType", deadlineType);
    formData.append("updatedBy", updatedBy);

    return async () => {
        try {
            const response = await axios.post(`${API_URL}/api/bid/opportunity/task/complete`,
                formData, {
                headers: {
                    "Content-Type": "application/json",
                },
            }
            );

            dispatch(getTasks());

            return response;
        } catch (error) {
            console.log(error)
            console.error(error);
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function changeAssignee(jobNumber, deadlineID, deadlineType, oldAssigneeID, newAssigneeID, newAssigneeName, updatedBy) {
    const API_URL = process.env.REACT_APP_API_URL;

    const formData = new FormData();
    formData.append("jobNumber", Number(jobNumber));
    formData.append("deadlineID", Number(deadlineID));
    formData.append("deadlineType", deadlineType);
    formData.append("oldAssigneeID", oldAssigneeID);
    formData.append("newAssigneeID", newAssigneeID);
    formData.append("newAssigneeName", newAssigneeName);
    formData.append("updatedBy", updatedBy);

    return async () => {
        try {
            const response = await axios.post(`${API_URL}/api/bid/opportunity/task/change/assignee`,
                formData, {
                headers: {
                    "Content-Type": "application/json",
                },
            }
            );

            dispatch(getTasks());

            return response;
        } catch (error) {
            console.log(error)
            console.error(error);
            dispatch(slice.actions.hasError(error));
        }
    };
}

// Simple in-memory cache to handle race conditions
const profilePictureCache = new Map();
const ongoingRequests = new Map();

export function fetchProfilePictureCached(url) {
    return async () => {
        // Check if already cached
        if (profilePictureCache.has(url)) {
            return profilePictureCache.get(url);
        }

        // Check if request is already in progress
        if (ongoingRequests.has(url)) {
            return ongoingRequests.get(url);
        }

        // Create new request promise
        const requestPromise = (async () => {
            try {
                const API_URL = process.env.REACT_APP_API_URL;
                const response = await axios.get(`${API_URL}${url}`, { responseType: 'blob' });
                const imageUrl = URL.createObjectURL(response.data);

                // Cache the result
                profilePictureCache.set(url, imageUrl);

                return imageUrl;
            } catch (error) {
                console.error('Error fetching profile picture:', error);
                throw error;
            } finally {
                // Remove from ongoing requests
                ongoingRequests.delete(url);
            }
        })();

        // Store the promise to prevent duplicate requests
        ongoingRequests.set(url, requestPromise);

        return requestPromise;
    };
}

// Function to clear the cache
export function clearProfilePictureMemoryCache() {
    // Revoke blob URLs to prevent memory leaks
    for (const imageUrl of profilePictureCache.values()) {
        if (imageUrl.startsWith('blob:')) {
            URL.revokeObjectURL(imageUrl);
        }
    }
    profilePictureCache.clear();
    ongoingRequests.clear();
}